import { create } from 'zustand';
import { calculateWorstCase, calculateRSS, findStandard, iso2768m } from '../utils/calculations';
import { fromDeviations, makeElement, activeElements } from '../utils/elementShape';
import {
  DEFAULT_SAMPLES as MC_DEFAULT_SAMPLES,
  DEFAULT_CONFIDENCE as MC_DEFAULT_CONFIDENCE
} from '../utils/monteCarlo';
import { runMonteCarlo } from '../utils/mcRunner';
import { loadMcDefaults } from '../utils/mcDefaults';

// Resolved at module init time. The settings page mutates localStorage,
// and `newProject` re-reads it via the same loader — so any change made
// in /settings/montecarlo is picked up the next time the user creates a
// fresh project. Existing projects keep whatever they were saved with.
const INITIAL_MC_DEFAULTS = loadMcDefaults();

// Default-Block für den Wärmeausdehnungs-Modus (Task #27). Ausgeschaltet
// per Default, damit das Feature opt-in bleibt und die Berechnung
// identisch zu Pre-#27-Verhalten läuft, solange nichts angeschaltet wird.
// Tmin/-max-Defaults decken einen typischen Industrie-Bereich ab
// (Hallenklima Winter bis Motorabwärme), den User passt sie an.
const DEFAULT_THERMAL = Object.freeze({
  enabled: false,
  tMin: -20,
  tMax: 80,
});
const cloneThermal = (t) => ({ ...DEFAULT_THERMAL, ...(t || {}) });

// Default name shown for a fresh calculation. Was 'Neues Projekt' for
// historic reasons even though it's the *calculation* name, which was
// the source of considerable UX confusion ("Is the input above the
// chain a project name or a calculation name?"). Now: 'Neue Berechnung'.
const DEFAULT_CALCULATION_NAME = 'Neue Berechnung';

// Boot/reset state for the elements list. Used to ship with three
// sample rows for testing/screenshots; per user feedback the fresh
// state is now empty so first-time users see the onboarding hint
// instead of a pre-populated chain they didn't author.
const initialElements = () => [];

// When an element carries `norm`, the stored tol values are a cache of
// the norm formula's output for the current nominal. If either the
// nominal OR the norm itself changes we need to refresh that cache so
// downstream math sees the right deviations. Returns the merged
// element with refreshed tolMode/tol/tolUpper/tolLower (or the
// untouched element if no refresh is needed). Manual rows (norm='')
// pass through unchanged.
function refreshFromNorm(el) {
  if (!el?.norm) return el;
  const standard = findStandard(el.norm);
  if (!standard?.getTol) return el;
  try {
    const { lower, upper } = standard.getTol(el.nominal);
    if (!Number.isFinite(lower) || !Number.isFinite(upper)) return el;
    return { ...el, ...fromDeviations(lower, upper) };
  } catch {
    return el;
  }
}

export const useToleranceStore = create((set, get) => ({
  elements: initialElements(),
  m0: {
    nominal: 0,
    min: 0,
    max: 0,
    tolerance: 0,
    limits: { min: null, max: null },
    status: 'none'
  },
  mode: 'worstcase', // 'worstcase' | 'rss' | 'montecarlo'

  // ─── Calculation identity ───────────────────────────────────────────
  // Source of truth for the name shown in the ProjectBar's TR-XXX strip
  // and used as the default for newly-created Supabase rows. Renamed
  // from the misleading `projectName`. The *project* name lives in
  // projectStore.currentProject.name.
  calculationName: DEFAULT_CALCULATION_NAME,
  currentCalculationId: null,
  calculationIndex: null,
  // True when something has changed since the last save / load. Auto-save
  // (useAutoSave hook) and the manual Save button both look at this.
  isDirty: false,
  // Versions-Vorschau (Spec 2026-10-20): null oder { id, kind, label,
  // created_at } des angezeigten alten Stands. Solange gesetzt, sind
  // Inhaltsänderungen gesperrt und nichts wird gespeichert.
  preview: null,

  counted: false,        // Session has been counted/started (Free-plan concept)
  structureLocked: false, // Structure (add/remove/reorder) is locked

  // ─── Monte-Carlo parameters ──────────────────────────────────────
  // These live on the store (not the element list) so they're
  // per-calculation rather than per-element. They serialize into the
  // .tcp envelope (v2.1) so reproducibility round-trips.
  //
  // Initial values come from the user's saved defaults; engine
  // constants are the fallback if no preference is stored yet.
  mcSamples: INITIAL_MC_DEFAULTS.samples,         // 100 … 500_000
  mcConfidence: INITIAL_MC_DEFAULTS.confidence,   // 'p99_73' | 'p99_99' | 'p99_9999'
  mcSeed: null,                                   // null → Math.random; number → deterministic
  mcRunning: false,                               // true while a worker run is in flight

  // ─── Live-Calc vs. Manual snapshot ─────────────────────────────
  // Phase 2 (design reboot): the new SubBar exposes a Live ↔ Manuell
  // toggle. When `liveCalc` is true (default) the calculator behaves
  // as before — every change re-fires calculate(). When false, calc
  // results "freeze" until the user clicks the explicit Berechnen
  // button (computeNow). The gating wire-up happens in Phase 3 when
  // the new MeasureTable's edit handlers go in; for now liveCalc is
  // stored + read by the SubBar UI but doesn't actually pause the
  // existing calculate() invocations, so Phase 2 is a pure UI
  // addition with no behavior change.
  liveCalc: true,

  // ─── Wärmeausdehnung (Task #27) ──────────────────────────────────
  // Per-Berechnung-Settings für den thermischen Modus. Element-seitig
  // wird das Material in element.material gepflegt; die Auswertung
  // erfolgt in calculations.js, wenn thermal.enabled=true ist.
  // Serialisiert sich in das .tcp-Envelope v2.3 (siehe getCalculationData).
  thermal: cloneThermal(),

  // --- Actions ---

  addElement: (element) => {
    if (get().preview) return null;
    // If structure is locked, we don't allow simple adding
    if (get().structureLocked) return null;

    // makeElement normalizes to v2.2 shape and assigns a stable id.
    // Accepts either the canonical fields (tolMode + tol/tolUpper/
    // tolLower) or convenience deviations (lower/upper from a norm
    // formula) which it converts internally.
    const newElement = makeElement(element);

    set(state => ({
      elements: [...state.elements, newElement],
      isDirty: true
    }));

    get().calculate();
    // Returned so callers (e.g. MeasureTable's "+ Maß hinzufügen")
    // can autofocus the new row's Bezeichnung input — symmetric with
    // insertElementAfter.
    return newElement.id;
  },

  updateElement: (id, updates) => {
    if (get().preview) return;
    set(state => ({
      elements: state.elements.map(el => {
        if (el.id !== id) return el;
        let merged = { ...el, ...updates };
        // Norm/nominal/tol interplay — three routes back into the
        // norm-derived deviations cache:
        //   1. Caller changed `nominal` or `norm` without touching tol
        //      → refresh so the cached deviations follow the new
        //        nominal / new norm.
        //   2. Caller manually edited a tol field on a normed row →
        //      keep the override (refresh would clobber the user's
        //      typed value).
        //   3. Caller manually CLEARED all tol fields on a normed row
        //      (zero everywhere after the patch) → treat that as
        //      lifting the override and restore the norm-derived
        //      deviations. This is the contract the user expects when
        //      they wipe a manual value to "go back to the norm".
        const touchedTol = 'tolMode' in updates
          || 'tol' in updates
          || 'tolUpper' in updates
          || 'tolLower' in updates;
        if (touchedTol && merged.norm) {
          const cleared = (merged.tolMode === 'symmetric' && !merged.tol)
            || (merged.tolMode === 'asymmetric' && !merged.tolUpper && !merged.tolLower);
          if (cleared) merged = refreshFromNorm(merged);
        } else if (!touchedTol && ('nominal' in updates || 'norm' in updates)) {
          merged = refreshFromNorm(merged);
        }
        return merged;
      }),
      isDirty: true
    }));
    get().calculate();
  },

  removeElement: (id) => {
    if (get().preview) return;
    if (get().structureLocked) return;

    set(state => ({
      elements: state.elements.filter(el => el.id !== id),
      isDirty: true
    }));
    get().calculate();
  },

  reorderElements: (fromIndex, toIndex) => {
    if (get().preview) return;
    if (get().structureLocked) return;

    set(state => {
      if (toIndex < 0 || toIndex >= state.elements.length) return state;
      const newElements = [...state.elements];
      const [removed] = newElements.splice(fromIndex, 1);
      newElements.splice(toIndex, 0, removed);
      return { elements: newElements, isDirty: true };
    });
    get().calculate();
  },

  // Inserts a new element directly AFTER an existing one and returns
  // the new id so the caller can move focus onto it (e.g. Enter in
  // the tol cell adds a row and jumps the cursor to its Bezeichnung).
  // Falls back to append-at-end if the anchor id isn't found.
  insertElementAfter: (afterId, element) => {
    if (get().preview) return null;
    if (get().structureLocked) return null;
    const newElement = makeElement(element);
    set(state => {
      const idx = state.elements.findIndex(e => e.id === afterId);
      if (idx === -1) return { elements: [...state.elements, newElement], isDirty: true };
      const arr = [...state.elements];
      arr.splice(idx + 1, 0, newElement);
      return { elements: arr, isDirty: true };
    });
    get().calculate();
    return newElement.id;
  },

  setM0Limits: (limits) => {
    if (get().preview) return;
    set(state => ({
      m0: { ...state.m0, limits },
      isDirty: true
    }));
    get().calculate();
  },

  setMode: (mode) => {
    set({ mode, isDirty: true });
    get().calculate();
  },

  // Live-Calc switch + explicit Manuell trigger. Wired in Phase 3 to
  // actually gate auto-recompute; Phase 2 stores the value + provides
  // computeNow() so the SubBar can wire the UI today.
  setLiveCalc: (v) => set({ liveCalc: !!v }),
  computeNow: () => get().calculate(),

  // ─── Monte-Carlo parameter setters ───────────────────────────────
  setMcSamples: (n) => {
    const v = Math.max(100, Math.min(500_000, Math.round(Number(n) || MC_DEFAULT_SAMPLES)));
    set({ mcSamples: v, isDirty: true });
    if (get().mode === 'montecarlo') get().calculate();
  },

  setMcConfidence: (level) => {
    set({ mcConfidence: level, isDirty: true });
    if (get().mode === 'montecarlo') get().calculate();
  },

  // ─── Thermal-Setter (Task #27) ───────────────────────────────────
  setThermalEnabled: (v) => {
    if (get().preview) return;
    set(state => ({ thermal: { ...state.thermal, enabled: !!v }, isDirty: true }));
    get().calculate();
  },
  setThermalRange: (tMin, tMax) => {
    if (get().preview) return;
    // Reihenfolge tolerant: wenn User die Felder verdreht eintippt,
    // sortieren wir intern, damit die Berechnung deterministisch ist.
    const a = Number(tMin);
    const b = Number(tMax);
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    set(state => ({
      thermal: {
        ...state.thermal,
        tMin: Number.isFinite(lo) ? lo : state.thermal.tMin,
        tMax: Number.isFinite(hi) ? hi : state.thermal.tMax,
      },
      isDirty: true
    }));
    get().calculate();
  },
  setElementMaterial: (id, materialId) => {
    if (get().preview) return;
    set(state => ({
      elements: state.elements.map(el =>
        el.id === id ? { ...el, material: typeof materialId === 'string' ? materialId : '' } : el
      ),
      isDirty: true
    }));
    get().calculate();
  },

  setElementActive: (id, active) => {
    if (get().preview) return;
    // Bewusst auch bei structureLocked erlaubt: Deaktivieren ändert die
    // Struktur nicht (keine Zeile kommt hinzu oder fällt weg).
    set(state => ({
      elements: state.elements.map(el => el.id === id ? { ...el, inactive: !active } : el),
      isDirty: true
    }));
    get().calculate();
  },

  setMcSeed: (seed) => {
    // Empty string / null → unseeded (Math.random)
    if (seed === '' || seed === null || seed === undefined) {
      set({ mcSeed: null, isDirty: true });
    } else {
      const n = Number(seed);
      set({ mcSeed: Number.isFinite(n) ? n : null, isDirty: true });
    }
    if (get().mode === 'montecarlo') get().calculate();
  },

  calculate: (force = false) => {
    const { mode, m0, counted, mcSamples, mcConfidence, mcSeed, thermal } = get();
    // C5: inaktive Maße gehen in keine Methode ein (WC, RSS, MC).
    const elements = activeElements(get().elements);

    // Check if we should calculate:
    // Either it's forced, OR already counted, OR the components that call this
    // will check for Pro status. For now, the store just calculates if counted.
    if (!counted && !force) {
      // Clear results if not counted
      set({
        m0: { ...m0, nominal: 0, min: 0, max: 0, tolerance: 0, status: 'none' }
      });
      return;
    }

    if (elements.length === 0) {
      // Keine (aktiven) Maße: auch alte MC-/Thermik-Werte verwerfen und
      // einen laufenden MC-Durchgang ungültig machen (_mcRunId), sonst
      // landen Zahlen der vorherigen Kette im Export (C5-Review).
      set({
        m0: { ...m0, nominal: 0, min: 0, max: 0, tolerance: 0, status: 'none', mc: null, thermal: null },
        mcRunning: false,
        _mcRunId: (get()._mcRunId || 0) + 1
      });
      return;
    }

    const limits = m0.limits;

    if (mode === 'worstcase') {
      const result = calculateWorstCase(elements, limits, thermal);
      set({
        m0: {
          ...m0,
          nominal: result.nominal, min: result.min, max: result.max,
          tolerance: result.tolerance, status: result.status, mc: null,
          // thermal-Detail-Block (Tmin/Tmax-Shifts) wenn aktiv, sonst
          // null — die UI kann darauf reagieren und z.B. die zweite
          // Resultatzeile einblenden.
          thermal: result.thermal ?? null
        }
      });
      return;
    }

    if (mode === 'rss') {
      const result = calculateRSS(elements, limits, thermal);
      set({
        m0: {
          ...m0,
          nominal: result.nominal, min: result.min, max: result.max,
          tolerance: result.tolerance, status: result.status, mc: null,
          thermal: result.thermal ?? null
        }
      });
      return;
    }

    if (mode === 'montecarlo') {
      // MC runs through the runner — main-thread for small N, worker
      // for large N. We snapshot the structure so a fast follow-up
      // edit doesn't race against a slow worker reply.
      const runId = (get()._mcRunId || 0) + 1;
      set({ mcRunning: true, _mcRunId: runId });
      runMonteCarlo(structuredClone(elements), {
        limits,
        samples: mcSamples,
        confidence: mcConfidence,
        seed: mcSeed,
        // thermal wird im Worker als zusätzlicher Per-Sample-Shift
        // ausgewertet (Annahme: Baugruppe ist homogen warm, T ~ U(tMin,tMax)).
        thermal
      }).then((result) => {
        // Stale-result guard — only commit if this is the latest run.
        if (get()._mcRunId !== runId) return;
        set(state => ({
          mcRunning: false,
          m0: {
            ...state.m0,
            nominal: result.nominal,
            min: result.min,
            max: result.max,
            tolerance: result.tolerance,
            status: result.status,
            mc: {
              mean: result.mean,
              stddev: result.stddev,
              capability: result.capability,
              percentiles: result.percentiles,
              bandLow: result.bandLow,
              bandHigh: result.bandHigh,
              histogram: result.histogram,
              sampleCount: result.sampleCount,
              confidence: result.confidence,
              k: result.k,
              seed: result.seed
            }
          }
        }));
      }).catch((err) => {
        console.error('Monte-Carlo run failed:', err);
        if (get()._mcRunId !== runId) return;
        set({ mcRunning: false });
      });
    }
  },

  // Internal counter for invalidating stale async MC results.
  _mcRunId: 0,

  // ─── Session / lifecycle actions ────────────────────────────────────

  /**
   * Starts a Free-plan session: flips `counted` so the ResultsPanel
   * unlocks. One monthly calculation is consumed by the caller
   * (authStore.incrementCalculation) BEFORE this runs.
   *
   * Originally this also set `structureLocked: true` to freeze the
   * chain after the first Berechnen, but user feedback said that's
   * too restrictive: a Free user should be able to keep refining the
   * chain (add/remove/reorder rows, change values) after seeing
   * results without consuming another credit. The counter ticks once
   * per Berechnen click — subsequent edits are "exploration" on the
   * same session. New chain → "Neue Berechnung" → another credit on
   * the next click.
   */
  startCalculation: () => {
    set({ counted: true, structureLocked: false });
    get().calculate();
  },

  /**
   * Pro-plan flip: marks the session as "active" (results visible)
   * without locking the structure. Pro users have no monthly cap, so
   * `counted` is just an internal "show results" gate here.
   */
  startCalculationPro: () => {
    set({ counted: true, structureLocked: false });
    get().calculate();
  },

  resetSession: () => {
    set({
      elements: [],
      m0: { nominal: 0, min: 0, max: 0, tolerance: 0, limits: { min: null, max: null }, status: 'none' },
      counted: false,
      structureLocked: false,
      isDirty: false,
      preview: null
    });
  },

  unlockStructure: () => {
    if (get().preview) return;
    // Unlocks the structure for editing without clearing existing elements
    set({
      counted: false,
      structureLocked: false
    });
  },

  /**
   * Loads a calculation into the store.
   *
   * @param {object} data         opaque calc payload (elements, m0, mode, …)
   * @param {string} name         calculation display name
   * @param {string|null} calcId  Supabase calculations.id (null for .tcp imports)
   * @param {number|null} index   TR-XXX index from the DB trigger
   * @param {object} [options]
   * @param {boolean} [options.proMode]       if true, leave structure editable
   * @param {boolean} [options.startCounted]  default true (saved projects /
   *   .tcp imports show their stored result right away). Set to false when
   *   the payload is a starting-point that the user still has to commit
   *   explicitly (e.g. a Free user picking a template — they get the chain
   *   pre-filled but no results until they hit BERECHNEN, which is what
   *   counts against their monthly budget).
   */
  loadProject: (data, name, calcId, index, options = {}) => {
    const proMode = options.proMode === true;
    const startCounted = options.startCounted !== false;
    // Re-canonicalize incoming elements through makeElement so any
    // fixture / template / legacy payload gets a clean v2.2 shape.
    // .tcp v2.2 files have already been schema-validated, but
    // template registry data ships hand-authored — running them
    // through makeElement is cheap insurance.
    const elements = (data.elements || []).map(makeElement);
    set({
      elements,
      calculationName: name || data.name || 'Geladene Berechnung',
      currentCalculationId: calcId || null,
      calculationIndex: index || null,
      m0: data.m0 || { nominal: 0, min: 0, max: 0, tolerance: 0, limits: { min: null, max: null }, status: 'none' },
      mode: data.mode || 'worstcase',
      mcSamples: data.mcSamples ?? MC_DEFAULT_SAMPLES,
      mcConfidence: data.mcConfidence ?? MC_DEFAULT_CONFIDENCE,
      mcSeed: data.mcSeed ?? null,
      // Fehlt im Payload (v2.2-Saves vor Task #27) → defaults, also
      // disabled. Element-seitig fehlt material ebenfalls — makeElement
      // setzt das auf '' und die Berechnung bleibt unverändert.
      thermal: cloneThermal(data.thermal),
      counted: startCounted,
      structureLocked: !proMode,
      isDirty: false,
      preview: options.preview ?? null
    });
    if (startCounted) get().calculate();
  },

  getCalculationData: () => {
    const { elements, m0, mode, mcSamples, mcConfidence, mcSeed, thermal } = get();
    return {
      elements,
      m0,
      mode,
      mcSamples,
      mcConfidence,
      mcSeed,
      thermal,
      // v2.3: optionales material pro Element + thermal-Block. Importer
      // älterer Versionen ignorieren die Zusatzfelder; v2.3-Importer
      // fallen auf Defaults zurück, wenn das Payload v2.2 ist.
      version: '2.3'
    };
  },

  newProject: () => {
    // Re-read defaults so a settings change applies on the very next
    // "New Project" without requiring a full page reload.
    const defaults = loadMcDefaults();
    set({
      elements: [],
      m0: { nominal: 0, min: 0, max: 0, tolerance: 0, limits: { min: null, max: null }, status: 'none' },
      calculationName: DEFAULT_CALCULATION_NAME,
      currentCalculationId: null,
      calculationIndex: null,
      mcSamples: defaults.samples,
      mcConfidence: defaults.confidence,
      mcSeed: defaults.seed ?? null,
      thermal: cloneThermal(),
      counted: false,
      structureLocked: false,
      isDirty: false,
      preview: null
    });
  },

  setCalculationName: (name) => {
    if (get().preview) return;
    set({ calculationName: name, isDirty: true });
  },

  /**
   * Called by the project-store after a successful save/PATCH so the
   * dirty flag clears without re-touching any of the actual content.
   */
  markClean: () => set({ isDirty: false }),

  /**
   * Imported from a .tcp file or template — the projectName field in
   * those legacy payloads maps to our calculationName. Kept as a thin
   * setter for the Header's load flow.
   */
  setNameFromImport: (name) => set({ calculationName: name }),

  addIsoElement: (nominal, name) => {
    if (get().preview) return;
    if (get().structureLocked) return;
    // ISO 2768-m is the default fast-add path. `makeElement` accepts
    // raw `lower`/`upper` and converts to the symmetric/asymmetric
    // storage shape internally; we tag the row with norm='iso2768m'
    // so the norm column / Beitragsleister can show the source.
    const tolerance = iso2768m(nominal);
    get().addElement({
      nominal,
      lower: tolerance.lower,
      upper: tolerance.upper,
      name: name || `M_${get().elements.length + 1}`,
      direction: 1,
      norm: 'iso2768m',
    });
  },
}));

// Re-exported so consumers don't hard-code the string.
export { DEFAULT_CALCULATION_NAME };
